/** «Себестоимость» на экранах раздела (до 22.10.2026 — «Закупочная цена») = хранимая себестоимость
 *  производства с НДС (`price_gross`) + ставка склада
 *  производства на дату (₽/шт, `warehouse_rates`). В данных склад не хранится в цене — он
 *  прибавляется здесь, по ставке на `valid_from` строки (решение владельца 21.10.2026). */
import React from "react";

import type { PriceSource, WarehouseRate } from "@/api/purchasePrices";
import { cn } from "@/lib/utils";
import { money, parseMoney, purchasePrice, rateOn } from "./format";
import { NO_RATE_TEXT, type RateOn } from "./rate";
import { ErrorBox } from "./ui";

/** Подпись хранимой части во второй строке: у расчёта производства — «производство». */
function storedLabel(source: PriceSource | null): string {
  if (source === null || source === "production") return "производство";
  return source === "contract" ? "контракт" : "вручную";
}

/** Ячейка «Себестоимость»: крупно — с складом, серым — из чего сложена. Ставки на дату нет —
 *  «—» и красный текст (цену без склада себестоимостью не называем). */
export function PurchasePriceCell({
  gross,
  source,
  rates,
  date,
  strong = false,
}: {
  gross: number;
  source: PriceSource | null;
  rates: WarehouseRate[] | undefined;
  /** дата, на которую берётся ставка (обычно `valid_from` цены) */
  date: string;
  strong?: boolean;
}): React.ReactElement {
  // ставки ещё грузятся или не загрузились (ошибку показывает таблица): без склада не складываем
  if (!rates) {
    return (
      <span className="inline-block text-right text-muted-foreground" data-testid="purchase-price">
        …<span className="block text-xs">{storedLabel(source)} с НДС {money(gross)}</span>
      </span>
    );
  }
  const rate = rateOn(rates, date);
  const full = purchasePrice(gross, rate);
  return (
    <span className="inline-block text-right" data-testid="purchase-price">
      <span className={cn(strong && "font-medium")}>{money(full)}</span>
      <span className="block text-xs font-normal text-muted-foreground">
        {storedLabel(source)} с НДС {money(gross)}
        {rate ? ` + склад ${money(rate.rub_per_unit)}` : ""}
      </span>
      {!rate && <span className="block text-xs text-red-600 dark:text-red-400">{NO_RATE_TEXT}</span>}
    </span>
  );
}

/** Живая строка под вводом себестоимости производства: «Себестоимость: X (+ склад Y)». Ставки на дату нет —
 *  красным `NO_RATE_TEXT` (форму это блокирует — см. `rateBlocks`). */
export function PurchaseLine({ raw, rate }: { raw: string; rate: RateOn }): React.ReactElement | null {
  if (rate.state === "loading") return <p className="text-xs text-muted-foreground">ставка склада загружается…</p>;
  if (rate.state === "error") return <ErrorBox error={rate.error} what="Ставка склада не загрузилась" />;
  if (rate.state === "none") {
    return (
      <p className="text-xs text-red-600 dark:text-red-400" role="alert" data-testid="purchase-line">
        {NO_RATE_TEXT} — сначала задайте ставку склада («Изменить» в шапке)
      </p>
    );
  }
  const p = parseMoney(raw, { positive: true });
  const r = rate.rate.rub_per_unit;
  return (
    <p className="text-sm" data-testid="purchase-line">
      Себестоимость: <b className="tabular-nums">{p.ok ? money(purchasePrice(p.num, rate.rate)) : "—"}</b>{" "}
      <span className="text-muted-foreground">(+ склад {money(r)})</span>
    </p>
  );
}
